import {
  mdiAccountHardHat,
  mdiAlert,
  mdiArrowTopRightThin,
  mdiCircleOutline,
  mdiDeleteSweep,
  mdiDrawPen,
  mdiElevator,
  mdiEraser,
  mdiExitRun,
  mdiFormatTextVariant,
  mdiHospitalBox,
  mdiMapLegend,
  mdiPalette,
  mdiSquareOutline,
  mdiVectorPolyline,
  mdiVectorSquare,
} from '@mdi/js';
import Icon from '@mdi/react';
import Button from 'components/Button/Button';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDrawingToolControlData,
  getSelectedIconData,
  getSelectedTool,
  resetControlData,
  setIsCreatingLegendItem,
  setSelectedArea,
  setSelectedArrow,
  setSelectedCircle,
  setSelectedIconData,
  setSelectedLegend,
  setSelectedLine,
  setSelectedSquare,
  setSelectedText,
  setSelectedTool,
} from '../../../store/lixaniPaint/slices/drawingToolSlice';
import { IconSelectionDialog } from './IconSelectionDialog';
import { GlobalSettings } from './ToolCustomizer/GlobalSettings/GlobalSettings';

export const ToolSelector = ({ availableTools }) => {
  const dispatch = useDispatch();
  const selectedTool = useSelector(getSelectedTool);
  const selectedIcon = useSelector(getSelectedIconData);
  const controlData = useSelector(getDrawingToolControlData);
  const [showIconSelection, setShowIconSelection] = useState(false);
  const [showGlobalStyles, setShowGlobalStyles] = useState(false);

  const icons = [
    { name: 'health', displayName: 'Ensiapu', icon: mdiHospitalBox },
    { name: 'elevator', displayName: 'Hissi', icon: mdiElevator },
    {
      name: 'emergency-exit',
      displayName: 'Hätäpoistumistie',
      icon: mdiExitRun,
    },
    { name: 'hard-hat', displayName: 'Kypärä', icon: mdiAccountHardHat },
    { name: 'alert', displayName: 'Hälytys', icon: mdiAlert },
  ];

  useEffect(() => {
    if (!selectedIcon.icon) {
      dispatch(setSelectedIconData(icons[0]));
    }
  }, []);

  const tools = [
    {
      name: 'pen',
      displayName: 'Kynä',
      icon: mdiDrawPen,
      instruction: 'Piirrä vapaalla kädellä',
    },
    {
      name: 'eraser',
      displayName: 'Pyyhekumi',
      icon: mdiEraser,
      instruction: 'Poista kynän luomia viivoja',
    },
    {
      name: 'line',
      displayName: 'Viiva',
      icon: mdiVectorPolyline,
      instruction:
        'Vasen painike aloittaa piirtämisen ja lisää kulmapisteitä, oikea painike viimeistelee viivan',
    },
    {
      name: 'arrow',
      displayName: 'Nuoli',
      icon: mdiArrowTopRightThin,
      instruction:
        'Ensimmäinen painike aloittaa nuolen piirron, toinen viimeistelee',
    },
    {
      name: 'area',
      displayName: 'Alue',
      icon: mdiVectorSquare,
      instruction:
        'Vasen painike aloittaa alueen piirron, ja lisää kulmapisteitä. Viimeistele alue painamalla aloituspistettä, oikea painike peruu alueen',
    },
    {
      name: 'text',
      displayName: 'Teksti',
      icon: mdiFormatTextVariant,
      instruction:
        'Lisää tekstiä vasemmalla painikkeella, kirjoita teksti kehotteeseen',
    },
    {
      name: 'square',
      displayName: 'Nelikulmio',
      icon: mdiSquareOutline,
      instruction:
        'Aloita piirtäminen vasemmalla painikkeella, viimeistele toisella painauksella. Oikea painike peruu piirtämisen',
    },
    {
      name: 'circle',
      displayName: 'Ympyrä',
      icon: mdiCircleOutline,
      instruction:
        'Klikkaa luodaksesi ympyrän. Vedä ja muuta kokoa valitun ympyrän kulmista',
    },
    {
      name: 'legend',
      displayName: 'Selite',
      icon: mdiMapLegend,
      instruction:
        'Aloita määrittely vasemmalla painikkeella, viimeistele toisella. Valitse kustomointipaneelista vasemmalta elementin valinta ja klikkaa elementtiä',
    },
    {
      name: 'icon',
      displayName: 'Ikoni',
      icon: selectedIcon.icon || mdiHospitalBox,
      instruction:
        'Lisää ikoni vasemmalla painikkeella, poista oikealla painikkeella',
    },
  ];

  const effectiveTools = tools.filter((tool) => {
    if (!availableTools) {
      return true;
    }
    return availableTools.includes(tool.name);
  });

  const handleToolSelection = (tool) => {
    if (tool === 'icon') {
      setShowIconSelection(true);
      return;
    }
    dispatch(setSelectedArea(null));
    dispatch(setSelectedArrow(null));
    dispatch(setSelectedCircle(null));
    dispatch(setSelectedLine(null));
    dispatch(setSelectedSquare(null));
    dispatch(setSelectedCircle(null));
    dispatch(setSelectedLegend(null));
    dispatch(setSelectedText(null));
    dispatch(setIsCreatingLegendItem(false));
    dispatch(setSelectedTool(tool));
  };

  const handleClearAll = () => {
    if (window.confirm('Haluatko varmasti tyhjentää?')) {
      dispatch(resetControlData());
    }
  };

  const modernToolButtonStyle = (isSelected) => ({
    padding: '8px 12px',
    margin: '2px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: isSelected ? '#1687af' : 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    fontSize: '10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
  });

  return (
    <div
      style={{
        backgroundColor: 'rgba(11, 113, 151, 0.9)',
        padding: '8px',
        borderRadius: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '4px',
        position: 'relative',
      }}
    >
      <Button elevated onClick={() => setShowGlobalStyles(!showGlobalStyles)}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <Icon path={mdiPalette} size={0.8} />
          <div
            style={{
              display: 'flex',
              gap: '1px',
            }}
          >
            <div
              style={{
                width: '6px',
                height: '4px',
                backgroundColor: controlData.lineColor || '#000000',
                borderRadius: '1px',
              }}
            />
            <div
              style={{
                width: '6px',
                height: '4px',
                backgroundColor: controlData.fillColor || '#0000ff',
                borderRadius: '1px',
              }}
            />
            <div
              style={{
                width: '6px',
                height: '4px',
                backgroundColor: controlData.textColor || '#000000',
                borderRadius: '1px',
              }}
            />
          </div>
        </div>
      </Button>

      {showGlobalStyles ? (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            marginTop: '4px',
            width: '300px',
            padding: '16px',
            backgroundColor: '#1687af',
            fontFamily: 'Segoe UI, sans-serif',
            zIndex: 999999999999,
            borderRadius: '8px',
          }}
        >
          <GlobalSettings></GlobalSettings>
        </div>
      ) : null}

      {effectiveTools.map((tool) => (
        <button
          key={tool.name}
          onClick={() => handleToolSelection(tool.name)}
          style={modernToolButtonStyle(selectedTool === tool.name)}
          title={`${tool.displayName} \n\n${tool.instruction}`}
          onMouseEnter={(e) => {
            if (selectedTool !== tool.name) {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedTool !== tool.name) {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
            }
          }}
        >
          <Icon path={tool.icon} size={1} />
        </button>
      ))}
      <button
        onClick={handleClearAll}
        style={{
          ...modernToolButtonStyle(false),
          backgroundColor: 'rgba(220, 53, 69, 0.8)',
        }}
        title="Tyhjennä kaikki piirretyt elementit"
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'rgba(220, 53, 69, 1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.8)';
        }}
      >
        <Icon path={mdiDeleteSweep} size={1} />
      </button>
      <IconSelectionDialog
        isOpen={showIconSelection}
        onClose={() => setShowIconSelection(false)}
      />
    </div>
  );
};
